import { useState } from 'react';
import { httpClient } from '../api/http';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert } from '@piggytechng/component-library';
import { queryParamBuilder } from '../helpers/utils';
import { SafelockSearchParams, SafelockSearchState } from '../types';

const useSafeLock = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [completedSafelocks, setCompletedSafelocks] = useState([]);
  const [pendingSafelocks, setPendingSafelocks] = useState([]);
  const [singleSafelockData, setSingleSafelockData] = useState<any>();
  const [safelockBalances, setSafelockBalances] = useState({
    dollarBalance: 0,
    nairaBalance: 0,
  });
  const [transactionsQueryType, setTransactionsQueryType] = useState<'fetch-all' | 'query'>();
  const [safelockTransactions, setSafelockTransctions] = useState<any>([]);

  const [paginationData, setPaginationData] = useState({
    currentPage: 0,
    nextPage: 1,
    lastPage: 1,
    perPage: 10,
    total: 0,
  });

  const params = useParams();
  const userIdIn = params.id as string;
  const safelockId = params.safelockId as string;

  const { data: safelockBalancesQ } = useQuery({
    queryKey: ['safelock-balances' + userIdIn],
    queryFn: () => httpClient.get(`admin/safelock/balance/${userIdIn}`).then((res) => res.data.data),
  });

  const { data: pendingSafelocksQ } = useQuery({
    queryKey: ['pending-safelock' + userIdIn],
    queryFn: () =>
      httpClient
        .get(`admin/safelock/pending/${userIdIn}/?currency=${'ngn'}&page=${1}&limit=${10}`)
        .then((res) => res.data.data),
  });

  const { data: completedSafelocksQ } = useQuery({
    queryKey: ['completed-safelock' + userIdIn],
    queryFn: () =>
      httpClient
        .get(`admin/safelock/completed/${userIdIn}/?currency=${'ngn'}&page=${1}&limit=${10}`)
        .then((res) => res.data.data),
  });

  const { data: singleSafelockDataQ } = useQuery({
    queryKey: ['single-safelock' + userIdIn + safelockId],
    enabled: !!safelockId,
    queryFn: () => httpClient.get(`admin/safelock/${safelockId}`).then((res) => res.data.data),
  });

  const { data: safelockTransactionsQ } = useQuery({
    queryKey: ['safelock-transactions' + userIdIn + safelockId],
    enabled: !!safelockId && !!userIdIn,
    queryFn: () =>
      httpClient.get(`admin/safelock/transactions/${safelockId}/${userIdIn}`).then((res) => res.data.data.transactions),
  });

  const [safelockSearch, setSafelockSearch] = useState<SafelockSearchState>({
    isActive: false,
    isLoading: false,
    list: [],
    total: 0,
  });

  /** GET admin/safelock/search/:userId/:status — status 0 = ongoing, 1 = completed. */
  const searchSafelocks = async (userId: string, status: number, filters: SafelockSearchParams) => {
    try {
      setSafelockSearch((prev) => ({ ...prev, isActive: true, isLoading: true }));

      const url = `admin/safelock/search/${userId}/${status}${queryParamBuilder({ ...filters })}`;
      const response = await httpClient.get(url);

      if (response.data.status) {
        const { list, total } = response.data.data;
        setSafelockSearch({ isActive: true, isLoading: false, list: list ?? [], total: total ?? 0 });
      } else {
        setSafelockSearch({ isActive: true, isLoading: false, list: [], total: 0 });
      }
    } catch (error) {
      console.log(error);
      setSafelockSearch({ isActive: true, isLoading: false, list: [], total: 0 });
    }
  };

  const clearSafelockSearch = () => {
    setSafelockSearch({ isActive: false, isLoading: false, list: [], total: 0 });
  };

  const fetchSafelockBalance = async (userId: string) => {
    try {
      setIsLoading(true);
      const url = `admin/safelock/balance/${userId}`;
      const response = await httpClient.get(url);
      if (response.data.status) {
        const { data } = response.data;
        setSafelockBalances(data);
      }
    } catch (error: any) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCompletedSafelocks = async (userId: string, currency: string, page: number, limit: number) => {
    try {
      setIsLoading(true);
      const url = `admin/safelock/completed/${userId}/?currency=${currency}&page=${page}&limit=${limit}`;
      const response = await httpClient.get(url);
      if (response.data.status) {
        const { data } = response.data;
        setCompletedSafelocks(data.list);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPendingSafelocks = async (userId: string, currency: string, page: number, limit: number) => {
    try {
      setIsLoading(true);
      const url = `admin/safelock/pending/${userId}/?currency=${currency}&page=${page}&limit=${limit}`;
      const response = await httpClient.get(url);
      if (response.data.status) {
        const { data } = response.data;
        setPendingSafelocks(data.list);
      }
    } catch (error: any) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSafelockData = async (safelockId: string) => {
    try {
      setIsLoading(true);
      const url = `admin/safelock/${safelockId}`;
      const response = await httpClient.get(url);
      if (response.data.status) {
        const { data } = response.data;
        setSingleSafelockData(data);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSafelockTransactions = async (userId: string, safelockId: string) => {
    try {
      const url = `admin/safelock/transactions/${safelockId}/${userId}`;
      const response = await httpClient.get(url);
      const { data } = response.data;
      setSafelockTransctions(data.transactions);
    } catch (error) {
      console.log(error);
    }
  };

  const breakSafelock = async (
    safelockId: string,
    payload: {
      identifier: string;
      password: string;
    },
    callBack?: () => void,
  ) => {
    try {
      setIsLoading(true);
      const response: any = await httpClient.post(`admin/safelock/break/${safelockId}`, payload);
      if (response.data.status) {
        Alert.success(response.data.message);
        if (callBack) callBack();
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const paySafelockInterest = async (
    safelockId: string,
    payload: {
      identifier: string;
      password: string;
    },
    callBack?: () => void,
  ) => {
    try {
      setIsLoading(true);
      const response: any = await httpClient.post(`admin/safelock/pay-interest/${safelockId}`, payload);
      if (response.data.status) {
        Alert.success(response.data.message);
        if (callBack) callBack();
      }
    } catch (error: any) {
      console.log(error);
      Alert.error(error?.response?.data?.message || 'Action failed');
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    fetchSafelockBalance,
    fetchCompletedSafelocks,
    fetchPendingSafelocks,
    fetchSafelockData,
    singleSafelockData,
    completedSafelocks,
    pendingSafelocks,
    safelockBalances,
    paginationData,
    setPaginationData,
    safelockTransactions,
    transactionsQueryType,
    setSafelockTransctions,
    setTransactionsQueryType,
    fetchSafelockTransactions,
    safelockBalancesQ,
    pendingSafelocksQ,
    singleSafelockDataQ,
    safelockTransactionsQ,
    completedSafelocksQ,
    breakSafelock,
    paySafelockInterest,
    safelockSearch,
    searchSafelocks,
    clearSafelockSearch,
  };
};

export default useSafeLock;
